<template>
  <div class="seo-card">
    <div class="seo-head">
      <div class="seo-head-top">
        <span class="seo-label-sm">SCORE SEO</span>
        <span class="seo-label-val" :class="'val-' + scoreVariant">{{ scoreLabel }}</span>
      </div>
      <div class="seo-ring-wrap">
        <svg class="seo-ring" viewBox="0 0 120 120">
          <circle cx="60" cy="60" r="50" fill="none" stroke="#e2e8f0" stroke-width="8" />
          <circle cx="60" cy="60" r="50" fill="none" :stroke="ringColor" stroke-width="8" stroke-linecap="round"
            :stroke-dasharray="circumference" :stroke-dashoffset="dashOffset"
            transform="rotate(-90 60 60)" class="seo-ring-fill" />
        </svg>
        <div class="seo-ring-num">
          <span class="seo-num" :class="'val-' + scoreVariant">{{ score }}</span>
          <span class="seo-pct">%</span>
        </div>
      </div>
    </div>

    <div class="seo-checks">
      <div v-for="item in checklist" :key="item.field" class="seo-check" :class="{ ok: item.ok }">
        <div class="seo-check-icon" :class="{ ok: item.ok }">
          <feather-icon :icon="item.ok ? 'CheckIcon' : 'XIcon'" size="12" />
        </div>
        <span class="seo-check-text">{{ item.label }}</span>
      </div>
    </div>

    <button v-if="score < 100" class="seo-cta" :disabled="optimizing" @click="$emit('optimize', failingChecks)">
      <b-spinner v-if="optimizing" small />
      <feather-icon v-else icon="ZapIcon" size="15" />
      <span>Otimizar com IA</span>
    </button>
    <div v-if="score >= 100" class="seo-perfect">
      <feather-icon icon="AwardIcon" size="16" />
      SEO perfeito!
    </div>
  </div>
</template>

<script>
import { BBadge, BProgress, BButton, BSpinner } from 'bootstrap-vue'

export default {
  name: 'SEOScoreCard',
  components: { BBadge, BProgress, BButton, BSpinner },
  props: {
    metaTitle: { type: String, default: '' },
    metaDescription: { type: String, default: '' },
    keywords: { type: String, default: '' },
    excerpt: { type: String, default: '' },
    featuredImage: { type: String, default: '' },
    content: { type: String, default: '' },
    optimizing: { type: Boolean, default: false },
  },
  data() {
    return { circumference: 2 * Math.PI * 50 }
  },
  computed: {
    checklist() {
      const titleLen = (this.metaTitle || '').length
      const descLen = (this.metaDescription || '').length
      const kwCount = (this.keywords || '').split(',').filter(k => k.trim()).length
      const excerptLen = (this.excerpt || '').length
      const wordCount = this.stripHtml(this.content || '').split(/\s+/).filter(Boolean).length
      const internalLinks = ((this.content || '').match(/href="\/[^"]*"/g) || []).length
      const externalLinks = ((this.content || '').match(/href="https?:\/\/[^"]*"/g) || []).length

      return [
        { field: 'meta_title', label: `Título SEO (${titleLen}/60)`, ok: titleLen >= 30 && titleLen <= 60 },
        { field: 'meta_description', label: `Meta Descrição (${descLen}/155)`, ok: descLen >= 120 && descLen <= 155 },
        { field: 'keywords', label: `Palavras-chave (${kwCount})`, ok: kwCount >= 3 },
        { field: 'excerpt', label: `Resumo (${excerptLen}/200)`, ok: excerptLen >= 50 && excerptLen <= 200 },
        { field: 'featured_image', label: 'Imagem Destacada', ok: !!this.featuredImage },
        { field: 'content_length', label: `Conteúdo (${wordCount} palavras)`, ok: wordCount >= 300 },
        { field: 'internal_links', label: `Links Internos (${internalLinks})`, ok: internalLinks >= 2 },
        { field: 'external_links', label: `Links Externos (${externalLinks})`, ok: externalLinks >= 1 },
      ]
    },
    score() {
      const passed = this.checklist.filter(c => c.ok).length
      return Math.round((passed / this.checklist.length) * 100)
    },
    scoreVariant() {
      if (this.score >= 80) return 'success'
      if (this.score >= 50) return 'warning'
      return 'danger'
    },
    scoreLabel() {
      if (this.score >= 80) return 'Excelente'
      if (this.score >= 50) return 'Regular'
      return 'Precisa Melhorar'
    },
    ringColor() {
      if (this.score >= 80) return '#10b981'
      if (this.score >= 50) return '#f59e0b'
      return '#ef4444'
    },
    dashOffset() {
      return this.circumference - (this.score / 100) * this.circumference
    },
    failingChecks() {
      return this.checklist.filter(c => !c.ok).map(c => c.field)
    },
  },
  methods: {
    stripHtml(html) {
      return html.replace(/<[^>]*>/g, '').trim()
    },
  },
}
</script>

<style scoped>
.seo-card{background:#fff;border:1px solid #e2e8f0;border-radius:14px;padding:20px;box-shadow:0 1px 3px rgba(0,0,0,.04)}
.seo-head{text-align:center;margin-bottom:18px}
.seo-head-top{display:flex;justify-content:space-between;align-items:center;margin-bottom:14px}
.seo-label-sm{font-size:.7rem;font-weight:700;text-transform:uppercase;letter-spacing:.8px;color:#94a3b8}
.seo-label-val{font-size:.72rem;font-weight:700;padding:3px 10px;border-radius:20px}
.seo-label-val.val-success{background:#ecfdf5;color:#10b981}
.seo-label-val.val-warning{background:#fffbeb;color:#f59e0b}
.seo-label-val.val-danger{background:#fef2f2;color:#ef4444}

.seo-ring-wrap{position:relative;width:110px;height:110px;margin:0 auto}
.seo-ring{width:100%;height:100%}
.seo-ring-fill{transition:stroke-dashoffset .6s cubic-bezier(.4,0,.2,1)}
.seo-ring-num{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;gap:1px}
.seo-num{font-size:1.8rem;font-weight:800;line-height:1}
.seo-pct{font-size:.85rem;font-weight:600;color:#94a3b8;margin-top:4px}
.val-success{color:#10b981}
.val-warning{color:#f59e0b}
.val-danger{color:#ef4444}

.seo-checks{display:flex;flex-direction:column;gap:5px;margin-bottom:16px}
.seo-check{display:flex;align-items:center;gap:8px;padding:7px 10px;border-radius:8px;transition:all .2s}
.seo-check.ok{background:#f8fafb}
.seo-check:not(.ok){background:#fef8f8}
.seo-check-icon{width:20px;height:20px;border-radius:6px;display:flex;align-items:center;justify-content:center;flex-shrink:0;color:#ef4444;background:#fef2f2}
.seo-check-icon.ok{color:#10b981;background:#ecfdf5}
.seo-check-text{font-size:.78rem;color:#475569;font-weight:500}

.seo-cta{display:flex;align-items:center;justify-content:center;gap:8px;width:100%;padding:11px;border:none;border-radius:10px;background:linear-gradient(135deg,#7c3aed,#a78bfa);color:#fff;font-size:.85rem;font-weight:700;cursor:pointer;transition:all .25s cubic-bezier(.4,0,.2,1);box-shadow:0 2px 10px rgba(124,58,237,.25)}
.seo-cta:hover:not(:disabled){transform:translateY(-1px);box-shadow:0 4px 18px rgba(124,58,237,.35)}
.seo-cta:disabled{opacity:.6;cursor:wait}

.seo-perfect{display:flex;align-items:center;justify-content:center;gap:6px;padding:10px;border-radius:10px;background:#ecfdf5;color:#10b981;font-weight:700;font-size:.85rem}
</style>
